import * as lendAPIConfigModule from './lendAPIConfig.json';

const lendAPIConfigRaw = (lendAPIConfigModule as any).default || lendAPIConfigModule;

/**
 * Defines the three types of LendAPI product funnels.
 *
 * - prequal: Pre-qualification funnel only
 * - fullApp: Full application funnel only
 * - combined: Combined prequal + full application funnel
 */
export enum LendAPIProductType {
  prequal = 'prequal',
  fullApp = 'fullApp',
  combined = 'combined',
}

/**
 * Defines a hook that triggers when a specific LendAPI event occurs.
 *
 * Hooks are used to fire Iterable events at specific points in the LendAPI funnel.
 *
 * @property type - The event type to listen for:
 *   - 'formSubmission': Triggers when a form is submitted on the specified tab
 *   - 'pageLoad': Triggers when the specified tab is loaded
 * @property triggerTab - The LendAPI tab name that triggers this hook (e.g., 'AboutYou', 'LoanDetails')
 * @property enabled - Optional master toggle for this hook, defaults to true when omitted
 */
export interface LendAPIHook {
  type: 'formSubmission' | 'pageLoad';
  triggerTab: string | string[];
  enabled?: boolean;
}

/**
 * Collection of hook configurations for different funnel stages.
 *
 * Each hook fires an Iterable event when triggered. All hooks are optional.
 *
 * @property prequalContactInfo - Fires when user provides contact info in prequal funnel
 * @property prequalComplete - Fires when prequal funnel is completed
 * @property fullAppContactInfo - Fires when user provides contact info in full application funnel
 * @property fullAppComplete - Fires when full application funnel is completed
 * @property enrichLendAPIApplication - Fires (BoatTrader-sourced loans only) to normalize the
 *   listing's boat/engine make/model against the JD Power catalog and PUT the resolved
 *   identity + trim ids onto the LendAPI application for JD Power page prefill (TRIDENT-971)
 * @property processCollateralURL - Fires on the Loan Info tab's formSubmission whenever a
 *   BoatTrader listing URL (boat_url) is present. Always persists the URL to the loan for
 *   Salesforce Collateral URL mapping; additionally triggers JD Power boat-details prefill
 *   (BoatTrader-sourced applications only) by IMT id when it differs from any prior
 *   click-through IMT id (TRIDENT-999)
 */
export interface LendAPIProductHooks {
  prequalContactInfo?: LendAPIHook;
  prequalComplete?: LendAPIHook;
  fullAppContactInfo?: LendAPIHook;
  fullAppComplete?: LendAPIHook;
  enrichLendAPIApplication?: LendAPIHook;
  processCollateralURL?: LendAPIHook;
}

/**
 * Configuration for a specific results page scenario in Google Analytics tracking.
 *
 * @property enabled - Whether to track this scenario
 * @property tabName - The LendAPI tab name where this result scenario appears
 */
export interface ResultScenarioConfig {
  enabled: boolean;
  tabName: string;
}

/**
 * Google Analytics tracking configuration for a LendAPI product.
 *
 * Controls GA event firing for various funnel events including pageviews and result scenarios.
 *
 * @property enabled - Master toggle for GA tracking on this product
 * @property resultsPageEvents - Configuration for tracking different result scenarios:
 *   - qualified: User qualified for a loan
 *   - unqualified: User credit profile was not found
 *   - declined: User's application was declined
 */
export interface FullAppSubmittedEventConfig {
  enabled: boolean;
  tabName: string;
}

export interface LeadSubmittedEventConfig {
  enabled: boolean;
  triggerTabs: string[];
}

export interface GATrackingConfig {
  enabled?: boolean;
  resultsPageEvents?: {
    qualified: ResultScenarioConfig;
    unqualified: ResultScenarioConfig;
    declined: ResultScenarioConfig;
  };
  fullAppSubmittedEvent?: FullAppSubmittedEventConfig;
  leadSubmittedEvent?: LeadSubmittedEventConfig;
}

/**
 * Kameleoon A/B testing goal conversion tracking configuration.
 *
 * @property enabled - Whether to track Kameleoon goal conversions for this product
 * @property customGoalMapping - Optional mapping of tab names to custom goal names.
 *   When a tab name exists in this mapping, the mapped goal name is used directly
 *   instead of the normalized pattern (custom-lendapi-{productSlug}-{normalizedTabName}).
 *   Example: { "Submitted": "custom-trident-loan-submit" }
 */
export interface KameleoonTrackingConfig {
  enabled: boolean;
  customGoalMapping?: Record<string, string>;
}

export interface PrequalCompleteForcedLoginConfig {
  enabled?: boolean;
  showBeforeTabs: string[];
  blur: boolean;
  text?: string;
}

export interface ResumeLinkForcedLoginConfig {
  enabled?: boolean;
  blur?: boolean;
  text?: string;
}

/**
 * Default configuration for a product type (prequal, fullApp, or combined).
 *
 * These configurations act as fallback defaults when individual products don't
 * specify their own configuration. Product-specific configs override these defaults.
 *
 * @property hooks - Default hook configurations for this product type
 * @property gaTracking - Default Google Analytics tracking configuration
 * @property kameleoonTracking - Default Kameleoon tracking configuration
 * @property prequalCompleteForcedLogin - Prequal complete forced login configuration (LendAPI funnels only)
 */
export interface LendAPIProductTypeConfig {
  hooks?: LendAPIProductHooks;
  gaTracking?: GATrackingConfig;
  kameleoonTracking?: KameleoonTrackingConfig;
  prequalCompleteForcedLogin?: PrequalCompleteForcedLoginConfig;
  resumeLinkForcedLogin?: ResumeLinkForcedLoginConfig;
}

/**
 * Configuration for a specific LendAPI product.
 *
 * Products inherit default configuration from their productType but can override
 * any setting at the product level.
 *
 * INHERITANCE STRATEGY:
 * - Product-specific hooks override productType hooks
 * - Product-specific gaTracking overrides productType gaTracking
 * - Product-specific kameleoonTracking overrides productType kameleoonTracking
 * - If a product doesn't specify a config, it falls back to productType defaults
 *
 * @property productId - Unique numeric identifier for this product from LendAPI
 * @property productSlug - URL-friendly identifier (format: alphanumeric_numeric, e.g., 'ZP3AZH8B_410')
 * @property productType - The type of funnel this product uses (prequal, fullApp, or combined)
 * @property hooks - Product-specific hook configurations (optional, inherits from productType)
 * @property gaTracking - Product-specific GA tracking config (optional, inherits from productType)
 * @property kameleoonTracking - Product-specific Kameleoon config (optional, inherits from productType)
 *
 * @example
 * // Product with custom hooks, inheriting GA/Kameleoon from productType
 * {
 *   productId: 626,
 *   productSlug: 'ZP3AZH8B_410',
 *   productType: 'fullApp',
 *   hooks: {
 *     fullAppContactInfo: { type: 'formSubmission', triggerTab: 'ContactInfo' }
 *   }
 * }
 */
export interface LendAPIProduct {
  productId: number;
  productSlug: string;
  productType: LendAPIProductType;
  hooks?: LendAPIProductHooks;
  gaTracking?: GATrackingConfig;
  kameleoonTracking?: KameleoonTrackingConfig;
  prequalCompleteForcedLogin?: PrequalCompleteForcedLoginConfig;
  resumeLinkForcedLogin?: ResumeLinkForcedLoginConfig;
  tags?: string[];
}

/**
 * Target product details for a portal CTA redirect.
 *
 * @property productType - The LendAPI funnel type the redirect lands on (prequal, fullApp, combined)
 * @property productId - The LendAPI product ID for the target funnel
 * @property productSlug - The product slug for the target funnel (e.g., 'F0Q8JUPD_468')
 */
export interface PortalCtaRedirectTarget {
  productType: LendAPIProductType;
  productId: number;
  productSlug: string;
}

/**
 * Configuration for a single portal CTA redirect rule.
 *
 * Used to redirect a portal's bare CTA URL (e.g., `/yacht-loans/apply/`) to a specific
 * LendAPI funnel before any React/Firebase initialization runs.
 *
 * @property hostname - The exact hostname this rule applies to (e.g., 'www.yachtworld.com')
 * @property pathPrefix - The exact path that triggers the redirect; serves as both the
 *   match prefix and the URL root used to build the target URL
 * @property enabled - Master toggle so the team can disable a rule without removing it
 * @property prodTestingInQA - QA-only toggle. When true on a QA entry, `prodTesting=true`
 *   is injected into the redirect target so a `prodTestingInQA`-tagged product loads its
 *   iframe against the production LendAPI origin without logging a console error. When
 *   false or absent, any incoming `prodTesting` query param is stripped from the redirect
 *   target URL. Semantically meaningful only on QA entries; should not appear on
 *   production entries.
 * @property requireQueryParams - Optional record of query-param keys mapped to their
 *   required values. When set, every key must be present in the URL search string with
 *   the expected value (compared case-insensitively, e.g. `Boat`/`boat`/`BOAT` all
 *   match `Boat`) for the redirect to fire. Param-name lookup is still case-sensitive
 *   per WHATWG `URLSearchParams.get`. When omitted, no query-param constraint is
 *   applied.
 * @property target - The LendAPI funnel target details
 */
export interface PortalCtaRedirect {
  hostname: string;
  pathPrefix: string;
  enabled: boolean;
  prodTestingInQA?: boolean;
  requireQueryParams?: Record<string, string>;
  target: PortalCtaRedirectTarget;
}

/**
 * Root configuration structure for all LendAPI products.
 *
 * This configuration is loaded from lendAPIConfig.json and provides centralized
 * management of LendAPI product configurations across different environments.
 *
 * CONFIGURATION HIERARCHY:
 * 1. productTypes define default configurations for each funnel type
 * 2. Individual products can override productType defaults
 * 3. Products are separated by environment (qa vs production)
 *
 * HOW TO ADD A NEW PRODUCT:
 * 1. Add the product to either products.qa or products.production array in lendAPIConfig.json
 * 2. Set productId (from LendAPI), productSlug (from URL), and productType
 * 3. Optionally override hooks, gaTracking, or kameleoonTracking
 * 4. The product will automatically inherit defaults from its productType
 *
 * HOW TO ADD A NEW PRODUCT TYPE:
 * 1. Add new enum value to LendAPIProductType
 * 2. Add corresponding config to productTypes in lendAPIConfig.json
 * 3. Define default hooks, gaTracking, and kameleoonTracking for the new type
 *
 * @property productTypes - Default configurations for each product type (prequal, fullApp, combined)
 * @property products - Environment-specific product arrays (qa and production)
 */
export interface LendAPIConfig {
  productTypes: {
    prequal: LendAPIProductTypeConfig;
    fullApp: LendAPIProductTypeConfig;
    combined: LendAPIProductTypeConfig;
  };
  products: {
    qa: LendAPIProduct[];
    production: LendAPIProduct[];
  };
  portalCtaRedirects?: {
    qa?: PortalCtaRedirect[];
    production?: PortalCtaRedirect[];
  };
}

export const lendAPIConfig: LendAPIConfig = lendAPIConfigRaw as any;

/**
 * Retrieves LendAPI products filtered by environment.
 *
 * @param environment - Optional environment filter ('qa' or 'production')
 *   - If 'qa': Returns only QA products
 *   - If 'production': Returns only production products
 *   - If undefined: Returns all products from both environments
 *
 * @returns Array of LendAPIProduct configurations
 *
 * @example
 * // Get all products
 * const allProducts = getAllProducts();
 *
 * // Get only production products
 * const prodProducts = getAllProducts('production');
 */
export function getAllProducts(environment?: 'qa' | 'production'): LendAPIProduct[] {
  if (environment === 'qa') {
    return lendAPIConfig.products.qa;
  }
  if (environment === 'production') {
    return lendAPIConfig.products.production;
  }
  return [...lendAPIConfig.products.qa, ...lendAPIConfig.products.production];
}

/**
 * Retrieves portal CTA redirect rules filtered by environment.
 *
 * @param environment - 'qa' or 'production' (stage uses 'qa')
 * @returns Array of `PortalCtaRedirect` rules for the environment, or an empty array
 *   if `portalCtaRedirects` is not configured.
 */
export function getPortalCtaRedirects(environment: 'qa' | 'production'): PortalCtaRedirect[] {
  return lendAPIConfig.portalCtaRedirects?.[environment] ?? [];
}

export interface LendAPIFullAppData {
  [key: string]: any;
  add_asset_2__1?: string | null;
  add_asset_2?: string | null;
  add_asset_3__1?: string | null;
  add_asset_4__1?: string | null;
  add_asset_5__1?: string | null;
  add_asset_6__1?: string | null;
  add_asset_7__1?: string | null;
  add_asset_8__1?: string | null;
  add_asset_9__1?: string | null;
  add_liability__1?: string | null;
  add_liability_2?: string | null;
  add_mortgage__1?: string | null;
  add_mortgage_2__1?: string | null;
  add_other_asset__1?: string | null;
  add_other_asset_2?: string | null;
  add_other_income__1?: string | null;
  add_other_income_2__1?: string | null;
  add_other_income_2?: string | null;
  add_other_income_3__1?: string | null;
  add_other_income_3?: string | null;
  add_other_income?: string | null;
  add_real_estate_2?: string | null;
  address__1?: string | null;
  address_unit?: string | null;
  address?: string | null;
  address2?: string | null;
  age?: number | null;
  app_id?: number | null;
  app_url_params?: Record<string, unknown> | null;
  app_url?: string | null;
  application_type?: string | null;
  are_you_us_citizen?: boolean | 'true' | 'false' | null;
  are_you_us_citizen__1?: boolean | 'true' | 'false' | null;
  asset_amount__1?: number | null;
  asset_amount_2?: number | null;
  asset_amount_3__1?: number | null;
  asset_amount_4__1?: number | null;
  asset_amount_5__1?: number | null;
  asset_amount_6__1?: number | null;
  asset_amount_7__1?: number | null;
  asset_amount_8__1?: number | null;
  asset_amount_9__1?: number | null;
  asset_amount?: number | null;
  base_rate?: number | null;
  birth_date__1?: string | null;
  birth_date?: string | null;
  boat_age__1?: number | null;
  boat_age_score?: number | null;
  boat_age?: number | null;
  boat_engine_count?: string | number | null;
  boat_fuel_type?: string | null;
  boat_make?: string | null;
  boat_model?: string | null;
  boat_modeltrimid?: number | null;
  boat_not_found?: boolean | 'true' | 'false' | null;
  boat_type?: string | null;
  boat_use?: string | null;
  boat_year?: number | string | null;
  boats_interest_rate?: number | null;
  boats_loan_amount__1?: number | null;
  boats_loan_amount?: number | null;
  boats_loan_term?: number | null;
  boats_monthly_interest_rate__1?: number | null;
  boats_monthly_interest_rate?: number | null;
  boats_monthly_payment?: number | null;
  boats_purchasing_power__1?: number | null;
  boats_purchasing_power?: number | null;
  business_phone__1?: string | null;
  business_phone?: string | null;
  business_type__1?: string | null;
  business_type?: string | null;
  calculated_loan_amount?: number | null;
  city__1?: string | null;
  city?: string | null;
  co_signer_1?: string | null;
  co_signer_2?: string | null;
  co_signer_3?: string | null;
  co_signer_4?: string | null;
  co_signer_5?: string | null;
  country__1?: string | null;
  country?: string | null;
  credit_consent__1?: boolean | null;
  credit_consent?: boolean | null;
  credit_score_modifier?: number | null;
  credit_score_score__1?: number | null;
  credit_score_score?: number | null;
  credit_score?: number | null;
  current_employment_status__1?: string | null;
  current_date?: string | null;
  custom?: string | null;
  dealer_broker_name?: string | null;
  derived_mailing?: string | null;
  derived_mailing__1?: string | null;
  derived_mailing_city?: string | null;
  derived_mailing_city__1?: string | null;
  derived_mailing_state?: string | null;
  derived_mailing_state__1?: string | null;
  derived_mailing_zip?: string | null;
  derived_mailing_zip__1?: string | null;
  desired_monthly_payment?: number | null;
  desired_term?: number | null;
  diff_mailing_address?: boolean | string | null;
  down_payment?: number | null;
  email__1?: string | null;
  email?: string | null;
  employed_months__1?: number | null;
  employed_months?: number | null;
  employed_years__1?: number | null;
  employed_years?: number | null;
  employer_name__1?: string;
  employer_name?: string;
  employment_status__1?: string | null;
  employment_status?: string | null;
  engine_make?: string | null;
  engine_manufacturer?: string | null;
  engine_model?: string | null;
  engine_modeltrimid?: number | null;
  engine_year?: number | string | null;
  engine?: string | number | null;
  engineCount?: string | null;
  estimated_down_payment?: number | null;
  extended_service_plan?: boolean | null;
  fico_rate_modifier?: number | null;
  financing_timeframe?: string | null;
  first_monthly_payment__1?: number | null;
  first_monthly_payment_2__1?: number | null;
  first_monthly_payment?: number | null;
  first_name__1?: string | null;
  first_name?: string | null;
  fromYear?: number | null;
  fuel?: string | null;
  fuelType?: string | null;
  battery_protection?: boolean | null;
  gap_protection?: boolean | null;
  have_trade_in?: boolean | 'true' | 'false' | null;
  home_monthly_payment__1?: number | null;
  home_monthly_payment?: number | null;
  horsepower?: number | null;
  initial_rate_adjustment?: number | null;
  interest_rate_1?: number | null;
  interest_rate_2?: number | null;
  interest_rate?: number | null;
  jd_power_base_averageretail__1?: number | null;
  jd_power_base_averageretail?: number | null;
  jd_power_base_highretail__1?: number | null;
  jd_power_base_highretail?: number | null;
  jd_power_base_tradein__1?: number | null;
  jd_power_base_tradein?: number | null;
  jd_power_beaminches__1?: number | null;
  jd_power_beaminches?: number | null;
  jd_power_categoryid__1?: number | null;
  jd_power_categoryid?: number | null;
  jd_power_categoryname__1?: string | null;
  jd_power_categoryname?: string | null;
  jd_power_hp__1?: number | null;
  jd_power_hp?: number | null;
  jd_power_hullmaterial__1?: string | null;
  jd_power_hullmaterial?: string | null;
  jd_power_lengthinches__1?: number | null;
  jd_power_lengthinches?: number | null;
  jd_power_marines_axels__1?: string | null;
  jd_power_marines_axels?: string | null;
  jd_power_marines_brakes__1?: boolean | null;
  jd_power_marines_brakes?: boolean | null;
  jd_power_marines_fueltype__1?: string | null;
  jd_power_marines_fueltype?: string | null;
  jd_power_marines_modeltype__1?: string | null;
  jd_power_marines_modeltype?: string | null;
  jd_power_marines_modeltypeid__1?: string | null;
  jd_power_marines_modeltypeid?: string | null;
  jd_power_modeltrimid__1?: number | null;
  jd_power_modeltrimid?: number | null;
  jd_power_modeltrimname__1?: string | null;
  jd_power_modeltrimname?: string | null;
  jd_power_modeltrimnote__1?: null;
  jd_power_modeltrimnote?: null;
  jd_power_numengines__1?: number | null;
  jd_power_numengines?: number | null;
  jd_power_series__1?: string | null;
  jd_power_series?: string | null;
  jd_power_suggestedlist__1?: number | null;
  jd_power_suggestedlist?: number | null;
  jd_power_total_averageretail__1?: number | null;
  jd_power_total_averageretail?: number | null;
  jd_power_total_highretail__1?: number | null;
  jd_power_total_highretail?: number | null;
  jd_power_total_tradein__1?: number | null;
  jd_power_total_tradein?: number | null;
  jd_power_weights__1?: number | null;
  jd_power_weights?: number | null;
  jd_power_year__1?: number | null;
  jd_power_year?: number | null;
  last_name__1?: string | null;
  last_name?: string | null;
  last_step_completed?: boolean | null;
  latest_outcome?: string | null;
  lendAPI?: string | null;
  lengthFeet?: string | null;
  lengthInches?: number | null;
  liability_balance_1__1?: number | null;
  liability_balance_1?: number | null;
  liability_institution_1__1?: string | null;
  liability_payment_1__1?: number | null;
  liability_payment_1?: number | null;
  loan_amount_score?: number | null;
  loan_to_value__1?: number | null;
  loan_to_value?: number | null;
  loan_type?: string | null;
  mailing_address?: string | null;
  mailing_city?: string | null;
  mailing_state?: string | null;
  mailing_zip_code?: string | null;
  mobile__1?: string | null;
  mobile?: string | null;
  make?: {
    name: string | null;
  } | null;
  make_year?: number | null;
  manufacturer?: string | null;
  marine_road_hazard_wheel_tire?: boolean | null;
  merchandise_apr?: string | null;
  middle_int__1?: string | null;
  middle_int?: string | null;
  mla?: string | null;
  model?:
    | {
        name: string | null;
      }
    | string
    | null;
  monthly_payment_1?: number | null;
  monthly_payment_2?: number | null;
  monthly_payment_3?: number | null;
  months__1?: number | null;
  months_at_address__1?: number | null;
  months_at_address?: number | null;
  months?: number | null;
  options?: Record<string, never> | null;
  other_asset__1?: string | null;
  other_asset_payment__1?: number | null;
  other_asset_payment?: number | null;
  other_asset_percentage__1?: number | null;
  other_asset_remain__1?: number | null;
  other_asset_value__1?: number | null;
  other_asset_value?: number | null;
  other_asset?: string | null;
  other_income_annual_1__1?: number | null;
  other_income_annual_1?: number | null;
  other_income_annual_2__1?: number | null;
  other_income_annual_2?: number | null;
  other_income_annual_3__1?: number | null;
  other_income_annual_3?: number | null;
  other_income_source_1__1?: string | null;
  other_income_source_1?: string | null;
  other_income_source_2__1?: string | null;
  other_income_source_2?: string | null;
  other_income_source_3__1?: string | null;
  other_income_source_3?: string | null;
  position__1?: string | null;
  position?: string | null;
  pre_qual_credit_score_range__1?: string | null;
  pre_qual_credit_score_range?: string | null;
  previous_address__1?: string | null;
  previous_address?: string | null;
  previous_city__1?: string | null;
  previous_city?: string | null;
  previous_employer_business_type__1?: string | null;
  previous_employer_business_type?: string | null;
  previous_employer_months__1?: number | null;
  previous_employer_months?: number | null;
  previous_employer_name__1?: string | null;
  previous_employer_name?: string | null;
  previous_employer_phone_number__1?: string | null;
  previous_employer_phone_number?: string | null;
  previous_employer_position__1?: string | null;
  previous_employer_position?: string | null;
  previous_employer_years__1?: number | null;
  previous_employer_years?: number | null;
  previous_monthly_payment__1?: number | null;
  previous_monthly_payment?: number | null;
  previous_months_address__1?: number | null;
  previous_months_address?: number | null;
  previous_state__1?: string | null;
  previous_state?: string | null;
  previous_type_of_residence__1?: string | null;
  previous_type_of_residence?: string | null;
  previous_years_address__1?: string | number | null;
  previous_years_address?: string | number | null;
  previous_zip__1?: string | null;
  previous_zip?: string | null;
  primary_income_annual__1?: number | null;
  primary_income_annual?: number | null;
  primary_income_source__1?: string | null;
  primary_income_source?: string | null;
  primary_signer?: string | null;
  product_referral?: string | null;
  purchase_price?: number | null;
  purchasing_power__1?: number | null;
  purchasing_power?: number | null;
  real_estate_type_1__1?: string | null;
  real_estate_type_1?: string | null;
  real_estate_type_2__1?: string | null;
  real_estate_type_2?: string | null;
  real_estate_type_3?: string | null;
  real_estate_type_4?: string | null;
  real_estate_type_5?: string | null;
  real_estate_value__1?: number | null;
  real_estate_value_2__1?: number | null;
  real_estate_value?: number | null;
  remain_first_mortgage_amount__1?: number | null;
  remain_first_mortgage_amount_2__1?: number | null;
  requested_loan_amount?: number | null;
  retired_years__1?: number | null;
  retired_years?: number | null;
  sales_rep_name?: string | null;
  sales_tax?: number | null;
  same_current_address__1?: number | null;
  same_current_address_pri_co?: boolean | string | null;
  same_current_address?: number | null;
  secondary_income_annual__1?: number | null;
  secondary_income_annual?: number | null;
  secondary_income_source__1?: string | null;
  secondary_income_source?: string | null;
  ssn__1?: string | null;
  ssn?: string | null;
  state__1?: string | null;
  state?: string | null;
  tabs_covered?: number[] | null;
  tenant_address?: string | null;
  tenant_city?: string | null;
  tenant_name?: string | null;
  tenant_state?: string | null;
  tenant_zip_code?: string | null;
  test_derived?: number | null;
  test_mode?: string | null;
  title_ownership?: string | null;
  toYear?: number | null;
  trade_in_boat_type?: string | null;
  trade_in_engine_manufacturer?: string | null;
  trade_in_horsepower?: number | null;
  trade_in_manufacturer?: string | null;
  trade_in_model?: string | null;
  trade_in_year?: number | null;
  trailer?: boolean | 'true' | 'false' | null;
  tri_down_payment__1?: number | null;
  tri_down_payment?: number | null;
  tri_loan_amount__1?: number | null;
  tri_loan_amount?: number | null;
  tri_monthly__1?: number | null;
  tri_monthly?: number | null;
  tri_prequal_consumer_rate?: number | null;
  type_of_asset__1?: string | null;
  type_of_asset_2__1?: string | null;
  type_of_asset_2?: string | null;
  type_of_asset_3__1?: string | null;
  type_of_asset_3?: string | null;
  type_of_asset_4__1?: string | null;
  type_of_asset_4?: string | null;
  type_of_asset_5__1?: string | null;
  type_of_asset_5?: string | null;
  type_of_asset_6__1?: string | null;
  type_of_asset_6?: string | null;
  type_of_asset_7__1?: string | null;
  type_of_asset_7?: string | null;
  type_of_asset_8__1?: string | null;
  type_of_asset_8?: string | null;
  type_of_asset_9__1?: string | null;
  type_of_asset_9?: string | null;
  type_of_asset?: string | null;
  type_of_liability_1__1?: string | null;
  type_of_liability_1?: string | null;
  type_of_residence__1?: string | null;
  type_of_residence?: string | null;
  typeOfBoat?: string | null;
  undefined?: boolean | null;
  unlimited_roadside_assistance?: boolean | null;
  year?: number | null;
  years_at_address__1?: string | number | null;
  years_at_address?: string | number | null;
  zip_code__1?: string | null;
  zip_code?: string | null;
}
